/**
 * Assembles SMSWiser edit-batch payloads + optional prepared static-tag sync for the sent wizard.
 */

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import type { TriState } from '@/features/smswiser-campaigns/components/MultiCountryBulkEdit/shared/multiCountryBulkEdit.types';
import type {
  ClickIdStructureValue,
  DomainSchemeValue,
} from '@/features/smswiser-campaigns/utils/shortlinkOptionState';
import { buildSentDropPayloads } from '@/features/smswiser-campaigns/components/modals/sent/buildSentDropPayloads';
import { LEAVE_TLD_UNCHANGED } from '@/features/smswiser-campaigns/components/modals/sent/editSentDrop.constants';
import type {
  EditSentDropPayload,
  SentEditPreparedStaticSync,
} from '@/features/smswiser-campaigns/components/modals/sent/EditSentDropModal.types';

import type { CreativeApplyPayload } from './expandCreativeToPerDrop';
import { expandCreativeToPerDrop } from './expandCreativeToPerDrop';
import { mergeBulkEditScheduledSentIds } from './scheduledSentPrepared';

export interface SentWizardFormForPayload {
  propagateToCountries: string[];
  buildCreativePayload: () => CreativeApplyPayload;
  forceSend: TriState<boolean>;
  /** Optional: omitted = keep each drop's own shortlink options. */
  shortlink?: { clickIdStructure: ClickIdStructureValue; domainScheme: DomainSchemeValue };
  staticTagId: TriState<string | null>;
  overrides: Record<string, { routeId?: number; wiserId?: number; shortLinkTldValue?: string }>;
}

function staticTagSyncForDrops(
  dropsOrdered: PreparedDrop[],
  tag: TriState<string | null>,
): SentEditPreparedStaticSync[] | undefined {
  if (tag === 'unchanged' || tag === 'mixed') return undefined;
  return dropsOrdered.map((d) => ({
    preparedId: d.id,
    countryKey: d.countryKey,
    wiserId: d.wiserId,
    staticTagId: tag,
  }));
}

export function buildSentScheduledWizardSave(
  selectedIds: string[],
  mergedDrops: PreparedDrop[],
  form: SentWizardFormForPayload,
): { payloads: EditSentDropPayload[]; syncPreparedStaticTags?: SentEditPreparedStaticSync[] } {
  const orderedIds = mergeBulkEditScheduledSentIds(selectedIds, form.propagateToCountries, mergedDrops);
  const byId = new Map(mergedDrops.map((d) => [d.id, d]));
  const dropsOrdered = orderedIds.map((id) => byId.get(id)).filter((d): d is PreparedDrop => Boolean(d));
  const creativePayload = form.buildCreativePayload();
  const { perDropSmsTexts, perDropSenderNames } = expandCreativeToPerDrop(orderedIds, mergedDrops, creativePayload);
  const forceOut = form.forceSend === 'unchanged' || form.forceSend === 'mixed' ? 'unchanged' : form.forceSend;
  const payloads = buildSentDropPayloads({
    drops: dropsOrdered,
    isBulk: true,
    title: '',
    selectedSenderNames: creativePayload.senderNames ?? [],
    selectedSmsTexts: creativePayload.smsTexts ?? [],
    wiserId: 0,
    routeId: '',
    tldValue: LEAVE_TLD_UNCHANGED,
    forceSend: forceOut,
    useOnlyNewDomains: 'unchanged',
    clickIdStructure: form.shortlink?.clickIdStructure,
    domainScheme: form.shortlink?.domainScheme,
    perDropSmsTexts,
    perDropSenderNames,
    resolvers: {
      campaignWiserId: (d) => form.overrides[d.countryKey]?.wiserId ?? d.wiserId,
      routeId: (d) => {
        const ov = form.overrides[d.countryKey]?.routeId;
        if (ov != null) return ov;
        return d.routeId ?? '';
      },
      shortLinkTld: (d) => {
        const ov = form.overrides[d.countryKey]?.shortLinkTldValue;
        if (ov != null && ov.trim() !== '') return ov.trim();
        return d.shortLinkTldValue ?? '';
      },
    },
  });
  const syncPreparedStaticTags = staticTagSyncForDrops(dropsOrdered, form.staticTagId);
  return { payloads, syncPreparedStaticTags };
}
